import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../axiosConfig';
import {
    hasPushSecureContext,
    isPushSupported,
    registerPushServiceWorker,
    urlBase64ToUint8Array
} from '../pushNotifications';

const statusStyles = {
    success: { color: '#1b5e20', background: '#edf7ee', border: '1px solid #cfe8d3' },
    warning: { color: '#8a5300', background: '#fff7e6', border: '1px solid #f3d7a1' },
    error: { color: '#9f1c1c', background: '#fff1f1', border: '1px solid #f0c6c6' },
    info: { color: '#184a88', background: '#eef5ff', border: '1px solid #cddfff' }
};

export default function PushNotificationSettings() {
    const [busy, setBusy] = useState(false);
    const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
    const [subscribed, setSubscribed] = useState(false);
    const [configured, setConfigured] = useState(false);
    const [publicKey, setPublicKey] = useState('');
    const [feedback, setFeedback] = useState({ tone: 'info', text: '' });

    const supported = useMemo(() => isPushSupported(), []);
    const secureContext = useMemo(() => hasPushSecureContext(), []);

    const syncSubscriptionWithServer = useCallback(async (subscriptionObject) => {
        await axios.post(`${process.env.REACT_APP_API_URL}/push_subscription.php`, {
            action: 'save',
            subscription: subscriptionObject
        });
    }, []);

    const refreshPushState = useCallback(async () => {
        setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');

        if (!supported) {
            setConfigured(false);
            setSubscribed(false);
            return;
        }

        if (!secureContext) {
            setConfigured(false);
            setSubscribed(false);
            return;
        }

        try {
            const [registration, configResponse] = await Promise.all([
                registerPushServiceWorker(),
                axios.get(`${process.env.REACT_APP_API_URL}/push_subscription.php`)
            ]);

            const vapidPublicKey = String(configResponse.data?.vapid_public_key || '').trim();
            setPublicKey(vapidPublicKey);
            setConfigured(Boolean(configResponse.data?.configured && vapidPublicKey));

            const existingSubscription = registration ? await registration.pushManager.getSubscription() : null;
            setSubscribed(Boolean(existingSubscription));

            if (existingSubscription && vapidPublicKey) {
                try {
                    await syncSubscriptionWithServer(existingSubscription.toJSON());
                } catch (syncError) {
                    // Keep the UI usable even if the server sync fails transiently.
                }
            }
        } catch (error) {
            setConfigured(false);
            setSubscribed(false);
            setFeedback({
                tone: 'error',
                text: error?.response?.data?.error || 'Failed to load notification settings.'
            });
        }
    }, [secureContext, supported, syncSubscriptionWithServer]);

    useEffect(() => {
        refreshPushState();
    }, [refreshPushState]);

    const handleEnableNotifications = async () => {
        if (!supported || !secureContext) {
            return;
        }

        setBusy(true);
        setFeedback({ tone: 'info', text: '' });

        try {
            const permissionResult = permission === 'granted'
                ? 'granted'
                : await Notification.requestPermission();
            setPermission(permissionResult);

            if (permissionResult !== 'granted') {
                setFeedback({
                    tone: 'warning',
                    text: 'Browser permission was not granted. Please allow notifications to continue.'
                });
                return;
            }

            let vapidKey = publicKey;
            if (!vapidKey) {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/push_subscription.php`);
                vapidKey = String(response.data?.vapid_public_key || '').trim();
                setPublicKey(vapidKey);
                setConfigured(Boolean(response.data?.configured && vapidKey));
            }

            if (!vapidKey) {
                setFeedback({
                    tone: 'error',
                    text: 'The server is missing its VAPID public key configuration.'
                });
                return;
            }

            const registration = await registerPushServiceWorker();
            if (!registration) {
                setFeedback({
                    tone: 'error',
                    text: 'The push service worker could not be registered on this device.'
                });
                return;
            }

            let subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidKey)
                });
            }

            await syncSubscriptionWithServer(subscription.toJSON());
            setSubscribed(true);
            setFeedback({
                tone: 'success',
                text: 'Browser notifications are enabled for this device. Create a school-wide note to test them.'
            });
        } catch (error) {
            setFeedback({
                tone: 'error',
                text: error?.response?.data?.error || error?.message || 'Failed to enable notifications.'
            });
        } finally {
            setBusy(false);
        }
    };

    const handleDisableNotifications = async () => {
        if (!supported || !secureContext) {
            return;
        }

        setBusy(true);
        setFeedback({ tone: 'info', text: '' });

        try {
            const registration = await registerPushServiceWorker();
            const subscription = registration ? await registration.pushManager.getSubscription() : null;
            const endpoint = subscription?.endpoint || '';

            if (subscription) {
                await subscription.unsubscribe();
            }

            if (endpoint) {
                await axios.post(`${process.env.REACT_APP_API_URL}/push_subscription.php`, {
                    action: 'remove',
                    endpoint
                });
            }

            setSubscribed(false);
            setFeedback({
                tone: 'success',
                text: 'Browser notifications are disabled on this device.'
            });
        } catch (error) {
            setFeedback({
                tone: 'error',
                text: error?.response?.data?.error || error?.message || 'Failed to disable notifications.'
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="setting-group" style={{ marginBottom: '30px', padding: '20px', border: '1px solid #dde4f5', borderRadius: '8px', background: '#f7faff' }}>
            <h3 style={{ marginBottom: '15px', color: '#1f3d73' }}>Browser Notifications</h3>
            <p style={{ marginBottom: '15px', color: '#53657f', fontSize: '0.9em' }}>
                Turn on this device to receive school-wide notes as browser push notifications, even when Marksure is not open on the current tab.
            </p>

            {!supported ? (
                <div style={{ ...statusStyles.warning, padding: '12px 14px', borderRadius: '8px', fontSize: '0.9em' }}>
                    This browser does not support push notifications.
                </div>
            ) : !secureContext ? (
                <div style={{ ...statusStyles.warning, padding: '12px 14px', borderRadius: '8px', fontSize: '0.9em' }}>
                    Push notifications require HTTPS or localhost during development.
                </div>
            ) : !configured ? (
                <div style={{ ...statusStyles.warning, padding: '12px 14px', borderRadius: '8px', fontSize: '0.9em' }}>
                    The server VAPID keys are not configured yet, so this browser cannot subscribe.
                </div>
            ) : (
                <>
                    <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            <span style={{ padding: '6px 10px', borderRadius: '999px', background: subscribed ? '#e8f5e9' : '#eef2f7', color: subscribed ? '#1b5e20' : '#5c6b7b', fontSize: '0.85em', fontWeight: 600 }}>
                                {subscribed ? 'Subscribed on this device' : 'Not subscribed on this device'}
                            </span>
                            <span style={{ padding: '6px 10px', borderRadius: '999px', background: '#eef5ff', color: '#184a88', fontSize: '0.85em', fontWeight: 600 }}>
                                Permission: {permission}
                            </span>
                        </div>

                        {feedback.text ? (
                            <div style={{ ...statusStyles[feedback.tone] || statusStyles.info, padding: '12px 14px', borderRadius: '8px', fontSize: '0.9em' }}>
                                {feedback.text}
                            </div>
                        ) : null}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        <button
                            type="button"
                            onClick={handleEnableNotifications}
                            disabled={busy || subscribed}
                            style={{
                                padding: '10px 16px',
                                borderRadius: '6px',
                                border: '1px solid #2f5fd3',
                                background: subscribed ? '#dfe8ff' : '#2f5fd3',
                                color: subscribed ? '#6e85b5' : '#fff',
                                fontWeight: 'bold',
                                cursor: busy || subscribed ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {busy && !subscribed ? 'Enabling...' : 'Enable notifications'}
                        </button>

                        <button
                            type="button"
                            onClick={handleDisableNotifications}
                            disabled={busy || !subscribed}
                            style={{
                                padding: '10px 16px',
                                borderRadius: '6px',
                                border: '1px solid #c9d3e4',
                                background: '#fff',
                                color: '#46566c',
                                fontWeight: 'bold',
                                cursor: busy || !subscribed ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {busy && subscribed ? 'Disabling...' : 'Disable on this device'}
                        </button>
                    </div>

                    <p style={{ margin: 0, color: '#5f6f87', fontSize: '0.86em' }}>
                        Test path: enable this device, then create a school-wide note as an admin. Clicking the notification opens <code>/notes</code>.
                    </p>
                </>
            )}
        </div>
    );
}
